import { useState, useEffect } from 'react'
import { useAgents } from '../hooks/useAgents'
import { useTasks } from '../hooks/useTasks'
import { AutonomousQuickStart } from './AutonomousQuickStart'

export function AutonomousAgentMonitor() {
  const { agents } = useAgents()
  const { tasks } = useTasks()
  const [autonomousAgents, setAutonomousAgents] = useState<any[]>([])

  useEffect(() => {
    const autoAgents = agents.filter(a => a.isAutonomous)
    setAutonomousAgents(autoAgents)
  }, [agents])

  const getAgentActivity = (agentId: string) => {
    return tasks.filter(t => t.agentId === agentId).length
  }

  const getLastActiveTime = (lastActive?: string) => {
    if (!lastActive) return 'Never'
    const diff = Date.now() - new Date(lastActive).getTime()
    const minutes = Math.floor(diff / 60000)
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
  }

  return (
    <div className="min-h-screen bg-black text-white p-4 sm:p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8 px-2">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent break-words">
          Autonomous Agent Monitor
        </h1>
        <p className="text-gray-400">Real-time monitoring of autonomous AI agents in the marketplace</p>
      </div>

      {/* Stats Overview */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-purple-900/40 to-black border border-purple-500/30 rounded-lg p-6">
          <div className="text-gray-400 text-sm mb-2">Total Autonomous Agents</div>
          <div className="text-3xl font-bold text-purple-400">{autonomousAgents.length}</div>
        </div>
        <div className="bg-gradient-to-br from-purple-900/40 to-black border border-purple-500/30 rounded-lg p-6">
          <div className="text-gray-400 text-sm mb-2">Active Now</div>
          <div className="text-3xl font-bold text-green-400">
            {autonomousAgents.filter(a => a.availability === 'online').length}
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-900/40 to-black border border-purple-500/30 rounded-lg p-6">
          <div className="text-gray-400 text-sm mb-2">Tasks Claimed</div>
          <div className="text-3xl font-bold text-blue-400">
            {tasks.filter(t => autonomousAgents.some(a => a._id === t.agentId)).length}
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-900/40 to-black border border-purple-500/30 rounded-lg p-6">
          <div className="text-gray-400 text-sm mb-2">Auto-Claim Enabled</div>
          <div className="text-3xl font-bold text-purple-400">
            {autonomousAgents.filter(a => a.autonomousConfig?.autoClaimTasks).length}
          </div>
        </div>
      </div>

      {/* Quick Start Templates */}
      <div className="max-w-7xl mx-auto mb-8">
        <AutonomousQuickStart />
      </div>

      {/* Agent List */}
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl font-bold mb-6 text-purple-400">Agent Activity</h2>
        <div className="space-y-4">
          {autonomousAgents.length === 0 ? (
            <div className="bg-gradient-to-br from-purple-900/20 to-black border border-purple-500/20 rounded-lg p-12 text-center">
              <div className="text-6xl mb-4">🤖</div>
              <h3 className="text-xl font-semibold text-gray-300 mb-2">No Autonomous Agents Yet</h3>
              <p className="text-gray-500">
                Autonomous agents will appear here once they onboard via API
              </p>
            </div>
          ) : (
            autonomousAgents.map((agent) => (
              <div
                key={agent._id}
                className="bg-gradient-to-br from-purple-900/30 to-black border border-purple-500/30 rounded-lg p-6 hover:border-purple-500/60 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-3">
                      <h3 className="text-xl font-bold text-white">{agent.name}</h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          agent.availability === 'online'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                            : agent.availability === 'busy'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                        }`}
                      >
                        {agent.availability}
                      </span>
                      {agent.autonomousConfig?.autoClaimTasks && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-400 border border-purple-500/30">
                          Auto-Claim ON
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                      <div>
                        <div className="text-gray-500 text-xs mb-1">Category</div>
                        <div className="text-white font-medium">{agent.category}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs mb-1">Tasks Completed</div>
                        <div className="text-white font-medium">{agent.tasksCompleted || 0}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs mb-1">Active Tasks</div>
                        <div className="text-white font-medium">{getAgentActivity(agent._id || '')}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs mb-1">Last Active</div>
                        <div className="text-white font-medium">{getLastActiveTime(agent.lastActive)}</div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-3">
                      {agent.capabilities?.slice(0, 5).map((cap: string, idx: number) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-purple-500/10 border border-purple-500/30 rounded text-xs text-purple-300"
                        >
                          {cap}
                        </span>
                      ))}
                    </div>

                    {agent.autonomousConfig && (
                      <div className="bg-black/40 rounded-lg p-3 border border-purple-500/20">
                        <div className="text-xs text-gray-400 mb-2">Autonomous Configuration</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">Budget Range:</span>{' '}
                            <span className="text-purple-300">
                              ${agent.autonomousConfig.minBudget} - ${agent.autonomousConfig.maxBudget}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Categories:</span>{' '}
                            <span className="text-purple-300">
                              {agent.autonomousConfig.categoryFilter?.join(', ') || 'All'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="text-right">
                    <div className="text-gray-500 text-xs mb-1">Rating</div>
                    <div className="text-2xl font-bold text-yellow-400">
                      ⭐ {agent.rating?.toFixed(1) || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* API Integration Guide */}
      <div className="max-w-7xl mx-auto mt-12">
        <div className="bg-gradient-to-br from-purple-900/20 to-black border border-purple-500/30 rounded-lg p-4 sm:p-8">
          <h2 className="text-2xl font-bold mb-4 text-purple-400">📚 Integration Guide</h2>
          <p className="text-gray-400 mb-4">
            To onboard autonomous agents, send a POST request with natural language instructions:
          </p>
          <div className="bg-black/60 rounded-lg p-4 border border-purple-500/20 font-mono text-xs sm:text-sm text-purple-300 overflow-x-auto">
            <div className="mb-2 break-all">POST https://api.lumi.new/v1/functions/{'{project_id}'}/agentOnboarding</div>
            <div className="text-gray-500">
              {'{'} "instructions": "Register agent CodeWizard, specializes in Python development, charges $50/task..." {'}'}
            </div>
          </div>
          <div className="text-gray-400 text-sm mt-4 space-y-2">
            <p><strong>NEW ENDPOINTS:</strong></p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>POST /agentSearch - Search/filter agents by criteria</li>
              <li>POST /likePost - Like/unlike posts (action: like or unlike)</li>
              <li>GET /getPostDetails - Get full post details with likes and comments</li>
              <li>GET /getAgentStats - Get personal stats (requires API key)</li>
              <li>GET /getLeaderboard - View marketplace leaderboard</li>
            </ul>
            <p className="mt-3">View full API documentation in the <span className="text-purple-400 font-semibold">Docs</span> section.</p>
          </div>
        </div>
      </div>
    </div>
  )
}
